/* eslint-disable react-hooks/exhaustive-deps */
import { getDeviceId } from '@/commonFunctions/device'
import { Matches, Statistical } from '@/components/common'
import { MainContent } from '@/components/content'
import { MainLayout } from '@/components/layouts'
import { Profile } from '@/components/profile'
import { MainSidebar, MobileSidebar } from '@/components/sidebar'
import { ProfileLoading } from '@/components/skeleton'
import {
  LIMIT,
  OFFSET,
  SCROLLING_HEIGHT_ABS_ERROR,
  SCROLLING_THROTTLING_DURATION,
  SORT_ORDER,
} from '@/constants/Common'
import { useDebounce } from '@/hooks/useDebounce'
import { useAppSelector } from '@/hooks/useRedux'
import { appSelector } from '@/pages/home/selectors'
import { userSelector } from '@/pages/user/selectors'
import { registerDevice, useGetFollowingInfo, useGetMatchesHistory } from '@/services'
import { EventFormat, Match } from '@/types'
import { getMessaging, getToken, isSupported } from 'firebase/messaging'
import { throttle } from 'lodash'
import moment from 'moment'
import { useEffect, useState } from 'react'
import { authSelector } from '../auth/selectors'

export const Performance = () => {
  const { user } = useAppSelector(userSelector)
  const { isShowDropdown } = useAppSelector(appSelector)
  const { token } = useAppSelector(authSelector)

  const { mutate: getMatches, isLoading: isLoadingMatches, data: resMatchesHistory } = useGetMatchesHistory()
  const { data: resFollowingInfo, refetch: refetchFollowingInfo } = useGetFollowingInfo(user?.id, token)

  const [eventFormat, setEventFormat] = useState<EventFormat | null>(null)
  const [startDate, setStartDate] = useState<Date | null>(null)
  const [endDate, setEndDate] = useState<Date | null>(null)
  const [sortParameter, setSortParameter] = useState<'CREATED_DATE' | 'MODIFIED_DATE' | 'MATCH_DATE'>('MATCH_DATE')
  const [matches, setMatches] = useState<Match[]>([])
  const [offset, setOffset] = useState(OFFSET)
  const [isAbleToGetMatches, setIsAbleToGetMatches] = useState<boolean>(true)
  const [searchEventName, setSearchEventName] = useState('')

  const debouncedEventName = useDebounce<string>(searchEventName || '', 200)

  const onGetMatchesHistory = () => {
    if (user?.id)
      getMatches({
        userId: user?.id,
        filters: {
          eventFormat: eventFormat ? [eventFormat] : null,
          ...(debouncedEventName && { eventName: debouncedEventName }),
          ...(startDate &&
            endDate && {
              eventDate: {
                startDate: moment(startDate).format('YYYY-MM-DD'),
                endDate: moment(endDate).format('YYYY-MM-DD'),
              },
            }),
        },
        limit: LIMIT,
        offset: offset,
        sort: {
          order: SORT_ORDER,
          parameter: sortParameter,
        },
      })
  }

  const setupNotification = async () => {
    const supported = await isSupported()
    if (supported && user?.id) {
      const messaging = getMessaging()
      if (Notification.permission === 'default') {
        const permission = await Notification.requestPermission()
        if (permission === 'granted') {
          const token = await getToken(messaging)
          await registerDevice({ platform: 'web', uuid: getDeviceId(), token: token, userId: user.id })
        }
      } else if (Notification.permission === 'granted') {
        const token = await getToken(messaging)
        await registerDevice({ platform: 'web', uuid: getDeviceId(), token: token, userId: user.id })
      }
    }
  }

  const handleScroll = throttle(() => {
    if (
      Math.abs(window.innerHeight + document.documentElement.scrollTop - document.documentElement.offsetHeight) >
      SCROLLING_HEIGHT_ABS_ERROR
    ) {
      return
    }

    setOffset((pre) => pre + LIMIT)
    setIsAbleToGetMatches(true)
  }, SCROLLING_THROTTLING_DURATION)

  useEffect(() => {
    setOffset(OFFSET)
    setIsAbleToGetMatches(true)
    setMatches([])
  }, [eventFormat, sortParameter, startDate, endDate, debouncedEventName])

  useEffect(() => {
    if (resMatchesHistory) setMatches([...matches, ...resMatchesHistory.hits])
    if (resMatchesHistory?.hasMore) window.addEventListener('scroll', handleScroll)
    return () => {
      setIsAbleToGetMatches(false)
      window.removeEventListener('scroll', handleScroll)
    }
  }, [resMatchesHistory])

  useEffect(() => {
    setupNotification()
  }, [user])

  useEffect(() => {
    if (isAbleToGetMatches) {
      onGetMatchesHistory()
    }
  }, [user, isAbleToGetMatches, eventFormat, sortParameter, startDate, endDate, debouncedEventName])

  return (
    <>
      {!isShowDropdown && <MobileSidebar />}
      <MainLayout>
        <MainSidebar />
        <MainContent>
          <div className="flex flex-col mb-20 gap-y-6 lg:mb-0">
            {user && resFollowingInfo ? (
              <Profile
                data={user}
                followingInfo={resFollowingInfo?.result}
                refetchFollowingInfo={refetchFollowingInfo}
              />
            ) : (
              <ProfileLoading />
            )}
            <div className="flex w-full md:gap-x-6 xl:gap-x-16 gap-y-8 flex-col md:flex-row pb-6 justify-start">
              <Statistical
                userId={user?.id as number}
                statsDoubles={user?.stats?.doubles as string}
                statsSingles={user?.stats?.singles as string}
                statsSinglesReliability={user?.stats?.singlesReliabilityScore as string}
                statsDoublesReliability={user?.stats?.doublesReliabilityScore as string}
                statsProvisional={user?.stats?.provisionalRatings}
              />

              <Matches
                isLoading={isLoadingMatches}
                matches={matches}
                playerId={user?.id as number}
                eventFormat={eventFormat}
                setEventFormat={setEventFormat}
                setMatches={setMatches}
                setOffset={setOffset}
                sortParameter={sortParameter}
                setSortParameter={setSortParameter}
                startDate={startDate}
                endDate={endDate}
                setStartDate={setStartDate}
                setEndDate={setEndDate}
                searchText={searchEventName}
                setSearchText={setSearchEventName}
                isEmpty={
                  resMatchesHistory?.empty &&
                  !eventFormat &&
                  sortParameter === 'MATCH_DATE' &&
                  !startDate &&
                  !endDate &&
                  !searchEventName
                }
              />
            </div>
          </div>
        </MainContent>
      </MainLayout>
    </>
  )
}
